/* =========================================================
   TOTAL FOOTY CO — PRODUCT PAGE JS
   Gallery thumbnails, zoom modal, variant selection.

   REWRITTEN 2026-07-28. Four defects fixed:

   1. CURRENCY WAS GUESSED IN THE BROWSER.
      formatMoney() fell back to "USD" when Shopify.currency was unavailable,
      so a Canadian store could show US dollars the moment a shopper changed
      variant. All prices now arrive pre-formatted from Liquid's `money` filter
      via the data-product-json payload. There is no money formatter here at all.

   2. UPDATING THE PRICE DESTROYED THE PRICE MARKUP.
      `priceEl.textContent = ...` replaced the entire rendered price snippet —
      compare-at price and screen-reader labels included — with a bare string.
      We now update individual nodes via data- hooks (see snippets/price.liquid).

   3. ZOOM WAS UNREACHABLE BY KEYBOARD.
      The trigger was an <img role="button" tabindex="0">. Browsers do not fire
      a click from Enter/Space on an image, so keyboard users could focus it and
      nothing happened. The trigger is now a real <button>, and the modal traps
      focus, closes on Escape, and restores focus to the trigger on close.

   4. THE THUMBNAIL SWITCH LEFT A STALE srcset AND ALT.
      Setting only .src while a srcset was present meant the browser kept
      serving the previous image on high-DPR screens.
   ========================================================= */
(function () {
  "use strict";

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* ---------- Gallery thumbnail switching ---------- */
  document.addEventListener("click", function (e) {
    var thumb = e.target.closest("[data-gallery-thumb]");
    if (!thumb) return;
    var gallery = thumb.closest("[data-product-gallery]");
    if (!gallery) return;

    var mainImg = gallery.querySelector("[data-gallery-main-image]");
    if (mainImg && thumb.getAttribute("data-full-src")) {
      // Order matters: clear srcset before setting src, or the browser may keep
      // resolving the old candidate list and the image never visibly changes.
      mainImg.removeAttribute("srcset");
      mainImg.src = thumb.getAttribute("data-full-src");
      if (thumb.getAttribute("data-full-srcset")) {
        mainImg.setAttribute("srcset", thumb.getAttribute("data-full-srcset"));
      }
      if (thumb.getAttribute("data-full-alt")) {
        mainImg.setAttribute("alt", thumb.getAttribute("data-full-alt"));
      }
      if (thumb.getAttribute("data-zoom-src")) {
        mainImg.setAttribute("data-zoom-src", thumb.getAttribute("data-zoom-src"));
      }
    }

    qsa("[data-gallery-thumb]", gallery).forEach(function (t) {
      t.classList.remove("is-active");
      t.setAttribute("aria-pressed", "false");
    });
    thumb.classList.add("is-active");
    thumb.setAttribute("aria-pressed", "true");
  });

  /* ---------- Zoom modal ---------- */
  var zoomReturnFocus = null;

  function focusableIn(el) {
    return qsa(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      el
    );
  }

  function openZoom(trigger) {
    var modal = qs("#ProductZoomModal");
    if (!modal) return;
    var gallery = trigger.closest("[data-product-gallery]");
    var mainImg = gallery && gallery.querySelector("[data-gallery-main-image]");
    if (!mainImg) return;

    var img = qs("img", modal);
    img.src = mainImg.getAttribute("data-zoom-src") || mainImg.currentSrc || mainImg.src;
    img.alt = mainImg.getAttribute("alt") || "";

    zoomReturnFocus = trigger;
    modal.hidden = false;
    document.body.style.overflow = "hidden";

    var closeBtn = qs("[data-close-zoom]", modal);
    if (closeBtn) closeBtn.focus();
  }

  function closeZoom() {
    var modal = qs("#ProductZoomModal");
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    document.body.style.overflow = "";
    if (zoomReturnFocus && document.contains(zoomReturnFocus)) zoomReturnFocus.focus();
    zoomReturnFocus = null;
  }

  document.addEventListener("click", function (e) {
    var trigger = e.target.closest("[data-zoom-trigger]");
    if (trigger) { openZoom(trigger); return; }
    if (e.target.closest("[data-close-zoom]") || e.target.id === "ProductZoomModal") {
      closeZoom();
    }
  });

  document.addEventListener("keydown", function (e) {
    var modal = qs("#ProductZoomModal");
    if (!modal || modal.hidden) return;

    if (e.key === "Escape") { e.preventDefault(); closeZoom(); return; }

    // Keep focus inside the dialog while it is open.
    if (e.key === "Tab") {
      var items = focusableIn(modal);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* ---------- Variant picker ---------- */
  qsa("[data-variant-picker]").forEach(function (root) {
    var productJson = root.querySelector("[data-product-json]");
    if (!productJson) return;
    var product;
    try { product = JSON.parse(productJson.textContent); } catch (err) { return; }
    if (!product || !product.variants || !product.variants.length) return;

    var optionInputs = qsa("[data-option-input]", root);

    function getSelectedOptions() {
      return qsa("[data-option-input]:checked", root).map(function (input) { return input.value; });
    }

    function findVariant(options) {
      if (!options.length) return product.variants[0];
      return product.variants.filter(function (v) {
        return v.options.every(function (opt, i) { return opt === options[i]; });
      })[0];
    }

    /* Update a rendered price block in place, preserving its structure. */
    function paintPrice(container, variant) {
      if (!container) return;
      var wrap = container.querySelector("[data-price]");
      var current = container.querySelector("[data-price-current]");
      var compare = container.querySelector("[data-price-compare]");
      var compareLabel = container.querySelector("[data-price-a11y-compare]");

      if (current) {
        current.textContent = variant.price_formatted;
        current.className = variant.on_sale ? "price__sale" : "price__regular";
      }
      if (wrap) wrap.classList.toggle("price--on-sale", !!variant.on_sale);
      if (compare) {
        compare.textContent = variant.on_sale ? variant.compare_formatted : "";
        compare.hidden = !variant.on_sale;
      }
      if (compareLabel) compareLabel.hidden = !variant.on_sale;
    }

    function paintButton(btn, variant) {
      if (!btn) return;
      btn.disabled = !variant.available;
      var textNode = btn.querySelector("[data-atc-text]") || btn;
      textNode.textContent = variant.available
        ? btn.getAttribute("data-available-text")
        : btn.getAttribute("data-unavailable-text");
    }

    function updateUI() {
      var variant = findVariant(getSelectedOptions());
      var atcButton = root.querySelector("[data-add-to-cart]");
      var stickyButton = qs("[data-sticky-atc-submit]");
      var idInput = root.querySelector("[data-variant-id-input]");
      var availability = root.querySelector("[data-variant-availability]");

      if (!variant) {
        // Combination does not exist — disable rather than silently adding the
        // wrong variant to the cart.
        [atcButton, stickyButton].forEach(function (b) {
          if (!b) return;
          b.disabled = true;
          var t = b.querySelector("[data-atc-text]") || b;
          t.textContent = b.getAttribute("data-unavailable-text");
        });
        if (availability) availability.textContent = "";
        return;
      }

      if (idInput) idInput.value = variant.id;
      paintPrice(root.querySelector("[data-variant-price]"), variant);
      paintPrice(qs("[data-sticky-price]"), variant);
      paintButton(atcButton, variant);
      paintButton(stickyButton, variant);

      if (availability) {
        availability.textContent = variant.available
          ? ""
          : (atcButton && atcButton.getAttribute("data-unavailable-text")) || "";
      }

      // Swap the gallery image if this variant has its own media.
      var mainImg = qs("[data-gallery-main-image]");
      if (mainImg && variant.featured_src) {
        mainImg.removeAttribute("srcset");
        mainImg.src = variant.featured_src;
      }

      if (optionInputs.length) {
        var url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url);
      }
    }

    root.addEventListener("change", function (e) {
      if (e.target.closest("[data-option-input]")) updateUI();
    });

    // Only repaint on load when there is something to pick. On a single-variant
    // product the Liquid-rendered markup is already correct and touching it
    // would only risk a flash of re-written content.
    if (optionInputs.length) updateUI();
  });
})();
